/**
 * Componente de seleção de horário disponível - Design Premium
 * 
 * Exibe a lista de slots disponíveis para a data selecionada,
 * consultando a RPC get_available_scheduling_slots em tempo real.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 2.0.0 - Redesign premium com hover elevado e cor do evento
 */

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Clock, CalendarX } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAvailableSlots, type PublicEventType } from '@/hooks/usePublicScheduling';

interface PublicSchedulingSlotsProps {
  eventType: PublicEventType;
  selectedDate: string;
  onSlotSelect: (slotStart: string) => void;
  onBack: () => void;
}

export function PublicSchedulingSlots({
  eventType,
  selectedDate,
  onSlotSelect,
  onBack,
}: PublicSchedulingSlotsProps) {
  const { data: slots = [], isLoading, error } = useAvailableSlots(
    eventType.id,
    selectedDate,
    eventType.timezone
  );

  const eventColor = eventType.color || '#3B82F6';

  // Formata a data selecionada para exibição
  const formattedDate = format(parseISO(selectedDate), "EEEE, dd 'de' MMMM", {
    locale: ptBR,
  });

  return (
    <div className="p-6 sm:p-8">
      {/* Header premium com botão voltar */}
      <div className="flex items-center gap-3 mb-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full hover:bg-muted"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold text-foreground">
          Selecione um horário
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6 ml-11 capitalize font-medium">
        {formattedDate}
      </p>

      {/* Estado de carregamento premium */}
      {isLoading && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-xl" />
          ))}
        </div>
      )}

      {/* Erro genérico (rede, timeout, etc.) - v1.6.1 simplificado */}
      {error && !isLoading && (
        <div className="text-center py-14">
          <div
            className="h-14 w-14 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${eventColor}10` }}
          >
            <CalendarX className="h-6 w-6" style={{ color: eventColor }} />
          </div>
          <p className="text-sm font-semibold text-foreground">
            Erro ao buscar horários
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 max-w-[240px] mx-auto">
            Erro ao buscar horários disponíveis. Tente novamente.
          </p>
          <Button variant="outline" size="sm" className="mt-5 rounded-full" onClick={onBack}>
            Voltar ao calendário
          </Button>
        </div>
      )}

      {/* Sem slots disponíveis - estado vazio premium */}
      {!isLoading && !error && slots.length === 0 && (
        <div className="text-center py-14">
          <div
            className="h-14 w-14 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${eventColor}10` }}
          >
            <CalendarX className="h-6 w-6" style={{ color: eventColor }} />
          </div>
          <p className="text-sm font-semibold text-foreground">
            Nenhum horário disponível
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 max-w-[240px] mx-auto">
            Não há horários livres para esta data. Tente selecionar outra data.
          </p>
          <Button variant="outline" size="sm" className="mt-5 rounded-full" onClick={onBack}>
            Voltar ao calendário
          </Button>
        </div>
      )}

      {/* Lista de slots disponíveis - grid premium */}
      {!isLoading && !error && slots.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {slots.map((slot) => {
            const time = format(parseISO(slot.slot_start), 'HH:mm');
            return (
              <button
                key={slot.slot_start}
                type="button"
                className="h-12 rounded-xl text-sm font-semibold border border-border/60 bg-card text-foreground transition-all duration-200 hover:scale-[1.03] hover:shadow-md cursor-pointer"
                style={{
                  '--slot-color': eventColor,
                } as React.CSSProperties}
                onClick={() => onSlotSelect(slot.slot_start)}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.borderColor = eventColor;
                  el.style.backgroundColor = `${eventColor}08`;
                  el.style.color = eventColor;
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.borderColor = '';
                  el.style.backgroundColor = '';
                  el.style.color = '';
                }}
              >
                {time}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
